import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const STORAGE_KEY = 'horizon-wishlist';

/* ---------- WishlistService (singleton, pub/sub) ---------- */

class WishlistService {
  /** @type {Set<(handles: string[]) => void>} */
  #subscribers = new Set();

  /** @returns {string[]} */
  getItems() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') || [];
    } catch {
      return [];
    }
  }

  /** @param {string} handle */
  has(handle) {
    return this.getItems().includes(handle);
  }

  get count() {
    return this.getItems().length;
  }

  /** @param {string} handle */
  add(handle) {
    const items = this.getItems();
    if (!items.includes(handle)) {
      items.push(handle);
      this.#save(items);
    }
  }

  /** @param {string} handle */
  remove(handle) {
    const items = this.getItems().filter((h) => h !== handle);
    this.#save(items);
  }

  /** @param {string} handle */
  toggle(handle) {
    this.has(handle) ? this.remove(handle) : this.add(handle);
  }

  /** @param {(handles: string[]) => void} fn */
  subscribe(fn) {
    this.#subscribers.add(fn);
    return () => this.#subscribers.delete(fn);
  }

  /** @param {string[]} items */
  #save(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    this.#notify(items);
  }

  /** @param {string[]} items */
  #notify(items) {
    this.#subscribers.forEach((fn) => fn(items));
  }

  /** Listen for changes from other tabs */
  enableCrossTabSync() {
    window.addEventListener('storage', (e) => {
      if (e.key === STORAGE_KEY) {
        this.#notify(this.getItems());
      }
    });
  }
}

export const wishlistService = new WishlistService();
wishlistService.enableCrossTabSync();

/* ---------- WishlistButtonComponent ---------- */

class WishlistButtonComponent extends Component {
  /** @type {Function|null} */
  #unsubscribe = null;

  connectedCallback() {
    super.connectedCallback();
    const handle = this.handle;
    if (!handle) return;
    this.#updateIcon(wishlistService.has(handle));
    this.#unsubscribe = wishlistService.subscribe(() => {
      this.#updateIcon(wishlistService.has(/** @type {string} */ (this.handle)));
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe?.();
  }

  /** @returns {string | undefined} */
  get handle() {
    return this.dataset.productHandle;
  }

  /** @param {Event} event */
  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    const handle = this.handle;
    if (handle) wishlistService.toggle(handle);
  }

  /** @param {boolean} active */
  #updateIcon(active) {
    this.setAttribute('aria-pressed', String(active));
  }
}

if (!customElements.get('wishlist-button-component')) {
  customElements.define('wishlist-button-component', WishlistButtonComponent);
}

/* ---------- WishlistIconComponent ---------- */

class WishlistIconComponent extends Component {
  /** @type {Function|null} */
  #unsubscribe = null;

  connectedCallback() {
    super.connectedCallback();
    this.#update(wishlistService.count);
    this.#unsubscribe = wishlistService.subscribe((items) => {
      this.#update(items.length);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe?.();
  }

  /** @param {number} count */
  #update(count) {
    // <html> attribute drives icon visibility via CSS
    if (count > 0) {
      document.documentElement.setAttribute('data-wishlist-count', String(count));
    } else {
      document.documentElement.removeAttribute('data-wishlist-count');
    }
  }
}

if (!customElements.get('wishlist-icon-component')) {
  customElements.define('wishlist-icon-component', WishlistIconComponent);
}

/* ---------- Helpers ---------- */

/**
 * Collect section IDs from all cart-items-component elements in the DOM.
 * This mirrors the pattern used by product-form.js and component-cart-items.js.
 * @returns {string}
 */
function getCartSectionIds() {
  /** @type {Set<string>} */
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.add(el.dataset.sectionId);
    }
  });
  return Array.from(ids).join(',');
}

/**
 * @typedef {object} ShopifyProduct
 * @property {string} handle
 * @property {string} title
 * @property {{src: string}[]} [images]
 * @property {{src: string}} [image]
 * @property {ShopifyVariant[]} [variants]
 */

/**
 * @typedef {object} ShopifyVariant
 * @property {number} id
 * @property {string|number} price
 * @property {string|number|null} [compare_at_price]
 * @property {boolean} [available]
 */

/* ---------- WishlistDrawerComponent ---------- */

class WishlistDrawerComponent extends DialogComponent {
  /** @type {Map<string, ShopifyProduct>} */
  #productCache = new Map();

  /** @type {Function|null} */
  #unsubscribe = null;

  /** Translated UI strings from data-t-* attributes */
  get #t() {
    return {
      loading: this.dataset.tLoading || 'Loading...',
      addToBag: this.dataset.tAddToBag || 'Add to Bag',
      adding: this.dataset.tAdding || 'Adding...',
      added: this.dataset.tAdded || 'Added',
      soldOut: this.dataset.tSoldOut || 'Sold Out',
      error: this.dataset.tError || 'Error',
      emptyTitle: this.dataset.tEmptyTitle || 'Your wishlist is empty',
      emptySubtitle: this.dataset.tEmptySubtitle || 'Save your favourite items to come back to later.',
      remove: this.dataset.tRemove || 'Remove',
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.#unsubscribe = wishlistService.subscribe(() => {
      if (this.refs.dialog?.open) {
        if (wishlistService.count === 0) {
          this.closeDialog();
        } else {
          this.#renderItems();
        }
      }
      this.#updateHeaderCount();
    });
    this.#updateHeaderCount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe?.();
  }

  open() {
    this.showDialog();
    this.#renderItems();
  }

  close() {
    this.closeDialog();
  }

  #updateHeaderCount() {
    const count = wishlistService.count;
    const val = String(count);
    const countEl = this.querySelector('[ref="drawerCount"]');
    if (countEl) countEl.textContent = count > 0 ? `(${count})` : '';
    // Keep <html> attr + CSS custom property in sync
    if (count > 0) {
      document.documentElement.setAttribute('data-wishlist-count', val);
      document.documentElement.style.setProperty('--wl-count', `"${val}"`);
    } else {
      document.documentElement.removeAttribute('data-wishlist-count');
      document.documentElement.style.removeProperty('--wl-count');
    }
  }

  async #renderItems() {
    const container = /** @type {HTMLElement} */ (this.refs.wishlistItems);
    if (!container) return;

    const handles = wishlistService.getItems();

    if (handles.length === 0) {
      container.innerHTML = this.#emptyStateHTML();
      return;
    }

    container.innerHTML = `<div class="wishlist-drawer__loading">${this.#t.loading}</div>`;

    const products = await Promise.all(
      handles.map((handle) => this.#fetchProduct(handle))
    );
    const validProducts = /** @type {ShopifyProduct[]} */ (products.filter(Boolean));

    if (validProducts.length === 0) {
      container.innerHTML = this.#emptyStateHTML();
      return;
    }

    container.innerHTML = validProducts.map((p) => this.#itemHTML(p)).join('');

    container.querySelectorAll('[data-wishlist-remove]').forEach((/** @type {HTMLElement} */ btn) => {
      btn.addEventListener('click', (/** @type {Event} */ e) => {
        e.stopPropagation();
        const handle = /** @type {HTMLElement} */ (e.currentTarget).dataset.wishlistRemove;
        if (handle) wishlistService.remove(handle);
      });
    });

    container.querySelectorAll('[data-wishlist-atc]').forEach((/** @type {HTMLElement} */ btn) => {
      btn.addEventListener('click', (/** @type {MouseEvent} */ e) => this.#addToCart(e));
    });
  }

  /** @param {string} handle */
  async #fetchProduct(handle) {
    if (this.#productCache.has(handle)) return this.#productCache.get(handle);

    try {
      const res = await fetch(`/products/${handle}.json`);
      if (!res.ok) return null;
      const { product } = await res.json();
      this.#productCache.set(handle, product);
      return product;
    } catch {
      return null;
    }
  }

  /**
   * Add to cart using the same pattern as product-form.js:
   * - POST JSON to Theme.routes.cart_add_url
   * - Include sections param for Section Rendering API
   * - Dispatch CartAddEvent so cart icon + drawer update natively
   *
   * @param {MouseEvent} event
   */
  async #addToCart(event) {
    const btn = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const variantId = btn.dataset.wishlistAtc;
    if (!variantId) return;

    btn.disabled = true;
    btn.textContent = this.#t.adding;

    const sections = getCartSectionIds();

    try {
      const body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        ...(sections && { sections, sections_url: window.location.pathname }),
      });

      const res = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body })
      );
      const response = await res.json();

      if (response.status) {
        // Error from Shopify (e.g. out of stock)
        btn.textContent = response.description || this.#t.error;
        setTimeout(() => {
          btn.disabled = false;
          btn.textContent = this.#t.addToBag;
        }, 2000);
        return;
      }

      // Dispatch native CartAddEvent — cart icon and cart drawer
      // listen for this and update themselves automatically.
      // source = 'product-form-component' so cart-icon.js ADDs itemCount
      // to the current bubble count (any other source replaces it).
      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'product-form-component',
          itemCount: 1,
          sections: response.sections,
        })
      );

      btn.textContent = this.#t.added;
      // Close wishlist drawer so the cart drawer can open
      this.closeDialog();
    } catch {
      btn.disabled = false;
      btn.textContent = this.#t.addToBag;
    }
  }

  /** @param {ShopifyProduct} product */
  #itemHTML(product) {
    const variant = product.variants?.[0];
    const image = product.images?.[0]?.src || product.image?.src || '';
    const thumbSrc = image ? image.replace(/\.([^.]+)$/, '_200x.$1') : '';
    const price = variant ? this.#formatPrice(variant.price) : '';
    const comparePrice = variant?.compare_at_price ? this.#formatPrice(variant.compare_at_price) : '';
    const available = variant?.available !== false;
    const atcLabel = available ? this.#t.addToBag : this.#t.soldOut;

    return `
      <div class="wishlist-drawer__item">
        <a href="/products/${product.handle}" class="wishlist-drawer__item-media">
          ${thumbSrc ? `<img src="${thumbSrc}" alt="${this.#escapeHTML(product.title)}" loading="lazy" width="80" height="80">` : ''}
        </a>
        <div class="wishlist-drawer__item-details">
          <a href="/products/${product.handle}" class="wishlist-drawer__item-title">${this.#escapeHTML(product.title)}</a>
          <div class="wishlist-drawer__item-price">
            ${comparePrice ? `<span class="wishlist-drawer__compare-price">${comparePrice}</span>` : ''}
            <span${comparePrice ? ' class="wishlist-drawer__sale-price"' : ''}>${price}</span>
          </div>
          <button
            class="wishlist-drawer__atc-btn"
            data-wishlist-atc="${variant?.id || ''}"
            ${!available ? 'disabled' : ''}
          >${atcLabel}</button>
        </div>
        <button class="wishlist-drawer__remove-btn" data-wishlist-remove="${product.handle}" aria-label="${this.#t.remove} ${this.#escapeHTML(product.title)}">
          <svg width="11" height="13" viewBox="3.75 2.75 12.5 14.1" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>
            <path d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    `;
  }

  #emptyStateHTML() {
    return `
      <div class="wishlist-drawer__empty">
        <p class="wishlist-drawer__empty-title">${this.#t.emptyTitle}</p>
        <p class="wishlist-drawer__empty-subtitle">${this.#t.emptySubtitle}</p>
      </div>
    `;
  }

  /**
   * Format price from Shopify product JSON.
   * Product JSON returns price as string like "29.00".
   * @param {string|number} price
   */
  #formatPrice(price) {
    const num = typeof price === 'string' ? parseFloat(price) : price / 100;
    return new Intl.NumberFormat(Shopify.locale || 'en-US', {
      style: 'currency',
      currency: Shopify.currency?.active || 'USD',
    }).format(num);
  }

  /** @param {string} str */
  #escapeHTML(str) {
    const el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
